/**
 * `/help/navigation` — navigation and tabs.
 *
 * The port of the demo's Help "nav" tab (demo line 3911): how the three zones
 * of the window work, how the breadcrumb reads, what ⌘K searches, what the
 * persona picker changes, and the conventions worth knowing before reading any
 * figure on the platform.
 *
 * ## What changed, and why
 *
 * The demo's "Moving around" card described *its own* chrome — draggable pane
 * edges and remembered widths among it. This port runs on the AiRis ProjeX
 * foundation, whose chrome is different, and a help page that describes
 * controls the reader cannot find is worse than no help page. So the rows are
 * rewritten to describe **this** chrome: the rail, the scene column and the
 * assistant; the breadcrumb; ⌘K and what it searches; full width (F) and the
 * Expand view overlay, which the port does carry; the persona picker; and the
 * help anchors.
 *
 * "Conventions worth knowing" is carried verbatim — the condition bands, the
 * drive colours, live versus shift-end, the counters and the
 * drafts-not-actions rule are all still exactly true of the port — except
 * that a dotted figure opens its rows, not yet "the calculation behind it".
 * Restore those words when the source drawer shows a calculation.
 *
 * Static copy. No data, no transform: there is nothing to project.
 *
 * @see docs/logic/help/navigation.md
 */

import { HelpAnchor, SurfacePageBody, SurfacePageHeader, SurfacePageLayout } from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { HelpTabs } from './HelpTabs';

const STANDFIRST =
  'The window is three zones, the platform is three levels, and every page states where it sits. ' +
  'This page is what to click, what the numbers mean and what changes when you switch persona.';

interface HelpRow {
  readonly heading: string;
  readonly body: string;
}

/** The three zones of the window, in reading order. */
const ZONES: readonly HelpRow[] = [
  {
    heading: 'Left — the rail',
    body: 'Eight sections, each collapsing to its own heading. A number beside a row is what is open in it right now: alerts, drafts or jobs. Clicking a heading opens that section’s own page; clicking a row opens the page directly.',
  },
  {
    heading: 'Centre — the work area',
    body: 'One page at a time, with the breadcrumb locked above it and a help anchor in the top right of every surface. The anchor opens the build spec for that page — what it reads, what it derives and what it deliberately leaves out.',
  },
  {
    heading: 'Right — the assistant',
    body: 'Ask anything the pages can answer. Every reply names the queries it ran and carries source chips; clicking a chip opens the rows or the document the answer came from, so nothing has to be taken on trust.',
  },
];

/** Three levels and the breadcrumb — the demo's own explanation of the IA. */
const LEVELS: readonly HelpRow[] = [
  {
    heading: 'Section, view, entity',
    body: 'Three levels, always. A section is one of the eight in the rail; a view is a page inside it; an entity is one machine, one alert, one site or one document. Nothing is nested deeper than that.',
  },
  {
    heading: 'The breadcrumb',
    body: 'Locked to the top of the work area. It always reads Home › Section › View, with a fourth step on an entity page, and every step of it is clickable.',
  },
  {
    heading: 'Deep links work',
    body: 'Every view and every entity has its own address, including the filters on it — a division narrowed to one class, a document open in the data browser, an alert selected. Reloading or sharing the URL lands in the same place.',
  },
];

/** Moving around this chrome. */
const MOVING: readonly HelpRow[] = [
  {
    heading: 'Search',
    body: '⌘K or Ctrl-K from anywhere: machines, components, work orders, parts, people, cases, alerts, incidents, jobs, technicians, bulletins, documents and pages — and the text of every email, meeting and video transcript. Every word you type must match, and a word in a record’s title ranks it higher. Typing a machine id is the fastest route to it from any page.',
  },
  {
    heading: 'Full width & expand',
    body: 'Full width, in the top bar, hides the menu and the assistant together; F does the same from anywhere outside a text box, and pressing it again puts back what was open. Expand view, at the right of the breadcrumb, opens the current page full-screen: drag to move, wheel or − / + to zoom, Esc to close.',
  },
  {
    heading: 'Viewing as',
    body: 'The persona picker in the top bar. Switching re-weights the assistant’s suggested questions and lands you on that role’s own home page. Who uses what lists all six and what each opens.',
  },
  {
    heading: 'Suggested questions',
    body: 'The chips under a page header are that page’s questions for your persona. They ask the assistant with one click rather than making you phrase it.',
  },
  {
    heading: 'Guided tour',
    body: 'Help → Guided tour, or Start guided tour in the account menu. Seventeen stops drive the real pages, with Back, Next and End tour in a bar along the bottom; ← and → step while the bar has focus.',
  },
  {
    heading: 'Comments',
    body: 'Every substantial surface is commentable. Leave a note on a card and it is filed against that anchor, so review feedback arrives attached to the thing it is about.',
  },
];

/**
 * Demo line 3922 — "Conventions worth knowing", verbatim except "Dotted
 * figures", which drops "and the calculation" until the source drawer shows
 * one.
 */
const CONVENTIONS: readonly HelpRow[] = [
  {
    heading: 'Condition bands',
    body: 'Good 80–100 · fair 60–79 · poor 40–59 · very poor 20–39 · major issue under 20. A machine’s band is its lowest-scoring component.',
  },
  {
    heading: 'Drive colours',
    body: 'Diesel, battery-electric and autonomous each carry their own edge colour on every machine card and render.',
  },
  {
    heading: 'Dotted figures',
    body: 'Any number with a dotted underline opens the rows and the calculation behind it.',
  },
  {
    heading: 'Live vs shift-end',
    body: 'Three sites stream; three arrive at shift end through Pitram. The pill on each site says which.',
  },
  {
    heading: 'Counters in the menu',
    body: 'The number beside an item is what is open in it right now — jobs, alerts or drafts.',
  },
  {
    heading: 'Drafts, not actions',
    body: 'Anything an agent produced says so, and sits in Approvals until a person accepts it.',
  },
];

export function NavigationHelpScene() {
  return (
    <SurfacePageLayout width="full">
      <Commentable name="NavigationHelpScene">
        <SurfacePageBody className="@container max-w-[1320px] space-y-8" as="div">
          <SurfacePageHeader
            kicker="Help"
            title="Navigation & tabs"
            description={STANDFIRST}
            actions={<HelpAnchor doc="help/navigation" />}
            tabs={<HelpTabs current="nav" />}
          />

          <Commentable name="NavigationZones">
            <div className="grid grid-cols-1 gap-3 @3xl:grid-cols-3">
              <HelpCard title="Three zones" rows={ZONES} />
              <HelpCard title="Three levels, and the breadcrumb" rows={LEVELS} />
              <HelpCard title="Moving around" rows={MOVING} doc="workspace/search-and-view" />
            </div>
          </Commentable>

          <Commentable name="NavigationMoving">
            <section
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <h2
                className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                Tabs inside a page
              </h2>
              <p
                className="mt-2 text-[12px] leading-[1.6]"
                style={{ color: 'var(--ink-secondary)' }}
              >
                A page with more than one reading of the same subject uses tabs rather than a
                second page: a machine has its render, condition register, history, cost and
                Maximo record; a site has its fleet, people, parts, ground and open work; a
                division has its classes. A tab is part of the address, so a tab you share opens
                on the tab you were reading.
              </p>
            </section>
          </Commentable>

          <Commentable name="NavigationConventions">
            <section
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
            >
              <h2
                className="text-[10px] font-semibold uppercase tracking-[0.1em]"
                style={{ color: 'var(--ink-tertiary)' }}
              >
                Conventions worth knowing
              </h2>
              <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-3 @2xl:grid-cols-2 @5xl:grid-cols-3">
                {CONVENTIONS.map((row) => (
                  <div key={row.heading}>
                    <dt className="text-[12px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
                      {row.heading}
                    </dt>
                    <dd
                      className="mt-0.5 text-[11.5px] leading-[1.55]"
                      style={{ color: 'var(--ink-secondary)' }}
                    >
                      {row.body}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          </Commentable>
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}

function HelpCard({
  title,
  rows,
  doc,
}: {
  title: string;
  rows: readonly HelpRow[];
  /** Build spec for the controls the card describes, when there is one. */
  doc?: string;
}) {
  return (
    <section
      className="rounded-xl border p-4"
      style={{ borderColor: 'var(--hairline)', background: 'var(--surface-2)' }}
    >
      <div className="flex items-center justify-between gap-2">
        <h2
          className="text-[10px] font-semibold uppercase tracking-[0.1em]"
          style={{ color: 'var(--ink-tertiary)' }}
        >
          {title}
        </h2>
        {doc ? <HelpAnchor doc={doc} /> : null}
      </div>
      <dl className="mt-2.5 space-y-2.5">
        {rows.map((row) => (
          <div key={row.heading}>
            <dt className="text-[12px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
              {row.heading}
            </dt>
            <dd
              className="mt-0.5 text-[11.5px] leading-[1.55]"
              style={{ color: 'var(--ink-secondary)' }}
            >
              {row.body}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
